import type { TradingChallenge } from '@/hooks/v1/types/TradingChallenge'
import type { TradingChallengeResult } from '@/hooks/v1/types/TradingChallengeResult'

export function isOngoingChallengeResult(result?: TradingChallengeResult) {
  return (
    !result?.status ||
    !['BUSTED', 'COMPLETED', 'PRIZE_CLAIMED', 'TERMINATED'].includes(result.status)
  )
}

// Active competitions can suppress the notice. Once those have been checked,
// only the most recent completed participation can affect the banner.
export async function loadChallengeBannerResults(
  challenges: TradingChallenge[],
  currentUserId: string,
  currentUserName: string | undefined,
  loadResults: (challenge: TradingChallenge) => Promise<TradingChallengeResult[]>,
  signal?: AbortSignal,
) {
  const candidates = challenges
    .filter((challenge) => challenge.users_count !== 0)
    .sort((left, right) => Date.parse(right.start_at) - Date.parse(left.start_at))
  const ordered = [
    ...candidates.filter((challenge) => challenge.status === 'ACTIVE'),
    ...candidates.filter((challenge) => challenge.status === 'COMPLETED'),
  ]
  const results = new Map<string, TradingChallengeResult | undefined>()
  const normalizedName = currentUserName?.trim().toLocaleLowerCase()
  let latestParticipationStart = Number.NEGATIVE_INFINITY

  for (const challenge of ordered) {
    signal?.throwIfAborted()
    const start = Date.parse(challenge.start_at)
    if (challenge.status === 'COMPLETED' && start < latestParticipationStart) break

    const rows = await loadResults(challenge)
    signal?.throwIfAborted()
    const result = rows.find(
      (row) =>
        row.user_id === currentUserId ||
        (normalizedName && row.user_name?.trim().toLocaleLowerCase() === normalizedName),
    )
    results.set(challenge.id, result)
    if (!result && !challenge.users?.includes(currentUserId)) continue

    latestParticipationStart = Math.max(latestParticipationStart, start)
    if (challenge.status === 'ACTIVE' && isOngoingChallengeResult(result)) return results
  }

  return results
}
